'use client'

import { CompleteVerifyCreditButton } from '@/components/complete-verify-credit-button'
import Image from '@/components/image'
import { siteConfig } from '@/config/site'
import { GetUser } from '@/lib/api/hooks/use-user'
import { useCreatorStore } from '@/store'
import { App, Button, Modal, Typography } from 'antd'
import Cookies from 'js-cookie'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'

const { Title } = Typography

const Over18 = dynamic(() => import('@/components/over18'), {
  ssr: false,
})

const Activity = dynamic(() => import('@/components/activity'), {
  ssr: false,
})

const CompleteCategory = dynamic(
  () => import('@/components/complete-category'),
  {
    ssr: false,
  },
)

const CompleteProfile = dynamic(() => import('@/components/complete-profile'), {
  ssr: false,
})

const CompleteVerifyPhone = dynamic(
  () => import('@/components/complete-verify-phone'),
  {
    ssr: false,
  },
)

const CompleteVerify = dynamic(() => import('@/components/complete-verify'), {
  ssr: false,
})

// 定义显示配置的常量对象
const ShowConfig = {
  None: 0,
  Profile: 1,
  Category: 2,
  Verify: 3,
  ButtonBar: 4,
}

export default function Main({ children }) {
  const t = useTranslations()
  const { message, modal } = App.useApp()
  const router = useRouter()

  const { data: user, refetch: getUser } = GetUser()
  const setChannelId = useCreatorStore(state => state.setChannelId)

  const [over18, setOver18] = useState({
    open: false,
    key: siteConfig.over18Key,
  })

  const [activity, setActivity] = useState({
    open: false,
    key: siteConfig.activityKey,
  })

  useEffect(() => {
    const handleApiError = event => {
      const { detail } = event
      const { key, code } = detail
      console.error(`API error: ${code} - ${key}`)
      const messageKey = key === 'Server Panic' ? 'Server_Panic' : key
      if (code === 423) {
        Modal.destroyAll()
        modal.warning({
          content: (
            <p
              dangerouslySetInnerHTML={{
                __html: t.raw('error.block'),
              }}
            />
          ),
          footer: null,
          closable: true,
          maskClosable: true,
          afterClose: () => {
            Cookies.remove(siteConfig.tokenKey)
            setChannelId(undefined)
            router.push('/login')
          },
        })
        return
      }
      message.destroy()
      message.error(t(messageKey)).then(() => {
        if (code === 401) {
          Cookies.remove(siteConfig.tokenKey)
          setChannelId(undefined)
          router.push('/login')
        }
      })
    }

    window.addEventListener('apiError', handleApiError)

    return () => {
      window.removeEventListener('apiError', handleApiError)
    }
  })

  useEffect(() => {
    if (localStorage.getItem(over18.key) === null) {
      setOver18({ ...over18, open: true })
    }
    if (localStorage.getItem(activity.key) === null) {
      setActivity({ ...activity, open: true })
    }
    if (Cookies.get(siteConfig.tokenKey) !== undefined) {
      getUser()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 修改 setSetupVerifyPhone 調用方式，確保狀態更新
  const handleSetupVerifyPhone = value => {
    setSetupVerifyPhone(value)
  }

  const onConfirm = payload => {
    const { href, key, setState } = payload
    localStorage.setItem(key, new Date().getTime())
    setState(prev => ({ ...prev, open: false }))
    if (href) {
      router.push(href)
    }
  }

  const showProfile = useMemo(() => {
    if (user === undefined) {
      return false
    }
    if (user.nickname === '' || user.code === '' || user.birthday <= 0) {
      return true
    }
    return false
  }, [user])

  const showCategory = useMemo(() => {
    if (user === undefined) {
      return false
    }
    if (user.categories.length === 0) {
      return true
    }
    return false
  }, [user])

  // phone 尚未驗證 + 信用尚未驗證
  const showVerify = useMemo(() => {
    if (user === undefined) {
      return false
    }
    if (user.phone === '' && user.creditCardVerified === false) {
      return true
    }
    return false
  }, [user])

  const [setupVerifyPhone, setSetupVerifyPhone] = useState(false)

  const [showConfig, setShowConfig] = useState(ShowConfig.None)
  useEffect(() => {
    if (setupVerifyPhone) {
      setShowConfig(ShowConfig.None)
    } else if (showProfile) {
      setShowConfig(ShowConfig.Profile)
    } else if (showCategory) {
      setShowConfig(ShowConfig.Category)
    } else if (showVerify) {
      setShowConfig(ShowConfig.Verify)
    } else {
      setShowConfig(ShowConfig.None)
    }
  }, [showProfile, showCategory, showVerify, setupVerifyPhone])

  const showOver18 = over18.open

  return (
    <div className='w-dvw'>
      {children}
      <Over18
        open={showOver18}
        onConfirm={payload =>
          onConfirm({ ...payload, key: over18.key, setState: setOver18 })
        }
      />
      {false && (
        <Activity
          open={activity.open && !over18.open}
          onConfirm={payload =>
            onConfirm({ ...payload, key: activity.key, setState: setActivity })
          }
        />
      )}
      {showConfig === ShowConfig.Profile && (
        <CompleteProfile
          open={true}
          code={user.code}
          nickname={user.nickname}
          birthday={user.birthday}
          onFinish={getUser}
          onCancel={null}
        />
      )}
      {showConfig === ShowConfig.Category && (
        <CompleteCategory open={true} onFinish={getUser} />
      )}
      {/* 雙驗證確認 */}
      {showConfig === ShowConfig.Verify && (
        <CompleteVerify
          open={true}
          onCancel={() => {
            setShowConfig(
              siteConfig.shouldShowAuthBottomBar
                ? ShowConfig.ButtonBar
                : ShowConfig.None,
            )
          }}
          setupVerifyPhoneController={handleSetupVerifyPhone}
        />
      )}
      {/* 驗證電話 */}
      {setupVerifyPhone && (
        <CompleteVerifyPhone
          open={true}
          onFinish={getUser}
          setupVerifyPhoneController={handleSetupVerifyPhone}
        />
      )}
      {showConfig == ShowConfig.ButtonBar && (
        <div
          className='fixed bottom-0 left-0 z-[1000] flex w-full items-center bg-[#595968] px-[72px] py-[12px] shadow-[0_-2px_8px_rgba(0,0,0,0.5)]'
          style={{
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.5)',
          }}
        >
          <Image
            src='/logo.svg'
            alt='Mfans'
            width={327}
            height={120}
            className='mr-6 h-8 w-auto lg:h-10'
          />
          <Title className='mb-0 text-[#FF3C8E]' level={5}>
            {t('auth.pending.bottomBar.title')}
          </Title>
          <Button
            type='primary'
            onClick={() => setSetupVerifyPhone(true)}
            className='ml-auto mr-5'
          >
            {t('complete.verify.phone')}
          </Button>
          <CompleteVerifyCreditButton type='primary'>
            {t('complete.verify.credit_card')}
          </CompleteVerifyCreditButton>
        </div>
      )}
    </div>
  )
}
